import { CopyOutlined, LinkOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Empty, message, Modal } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "../../common/utils";
import projectDefault from "../../img/project_default.jpg";
import "./activity.scss";
import {
  fetchSaleActivityExpertLiveRoomInfo,
  fetchSaleActivityLiveRoomList,
  fetchSaleActivityUserEnterLiveRoomUrl,
} from "./api";
import {
  SaleActivityExpertLiveRoomInfo,
  SaleActivityLiveRoomInfo,
} from "./types";

const textOrDash = (value?: string | number | null) =>
  value === undefined || value === null || value === "" ? "--" : value;

type LiveRoomRole = "expert" | "user";

const getOperatorType = (roles: LiveRoomRole[]) => {
  if (roles.length !== 1) return undefined;
  return roles[0] === "expert" ? 1 : 2;
};

const uniqRoles = (roles: LiveRoomRole[]) =>
  roles.filter((role, index) => roles.indexOf(role) === index);

const parseRoleText = (value?: string | null): LiveRoomRole[] => {
  if (!value) return [];
  return value.split(/[,\s|;，、]+/).flatMap((item) => {
    const role = item.trim().toLowerCase();
    if (!role) return [];
    if (role === "expert" || role === "teacher" || role === "1")
      return ["expert"];
    if (
      role === "user" ||
      role === "student" ||
      role === "learner" ||
      role === "2"
    )
      return ["user"];
    if (role === "both" || role === "all" || role === "3")
      return ["expert", "user"];
    return [];
  });
};

const getLiveRoomRoles = (query: URLSearchParams): LiveRoomRole[] => {
  const roles = uniqRoles([
    ...query.getAll("type").flatMap(parseRoleText),
    ...query.getAll("role").flatMap(parseRoleText),
    ...query.getAll("roles").flatMap(parseRoleText),
    ...query.getAll("operatorType").flatMap(parseRoleText),
    ...query.getAll("currentOperatorType").flatMap(parseRoleText),
  ]);
  return roles.length > 0 ? roles : ["user"];
};

interface LiveRoomLinkInfo {
  liveRoomName?: string;
  liveUrl?: string;
  password?: string | number;
}

export default function SaleActivityLiveRoomList() {
  const query = useQuery();
  const roles = getLiveRoomRoles(query);
  const operatorType = getOperatorType(roles);
  const hasExpertRole = roles.includes("expert");
  const hasUserRole = roles.includes("user");
  const saleActivityId =
    query.get("saleActivityId") || query.get("activityId") || "";
  const saleActivityDetailId =
    query.get("saleActivityDetailId") || query.get("activityDetailId") || "";
  const liveActivityName = query.get("liveActivityName") || "直播授课";
  const cover = query.get("cover") || "";
  const [roomList, setRoomList] = useState<SaleActivityLiveRoomInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [linkModalInfo, setLinkModalInfo] = useState<LiveRoomLinkInfo | null>(
    null,
  );

  useEffect(() => {
    if (!saleActivityDetailId) return;
    setLoading(true);
    const params: {
      saleActivityId?: string;
      saleActivityDetailId: string;
      currentOperatorType?: number;
    } = {
      saleActivityId,
      saleActivityDetailId,
    };
    if (operatorType) params.currentOperatorType = operatorType;
    fetchSaleActivityLiveRoomList(params)
      .then((res) => {
        const data = res.data || {};
        setRoomList(data.liveRoomList || []);
      })
      .finally(() => setLoading(false));
  }, [saleActivityId, saleActivityDetailId, operatorType]);

  const roomCountText = useMemo(
    () => `${roomList.length} 个直播间`,
    [roomList.length],
  );
  const liveUrl = linkModalInfo?.liveUrl;

  const getRoomPassword = (room: SaleActivityLiveRoomInfo) =>
    room.password ||
    room.loginPassword ||
    room.livePassword ||
    room.roomPassword;

  const getExpertRoomLinkInfo = (room: SaleActivityLiveRoomInfo) =>
    fetchSaleActivityExpertLiveRoomInfo({
      saleActivityId: room.saleActivityId || saleActivityId,
      saleActivityDetailId: room.saleActivityDetailId || saleActivityDetailId,
      liveActivityRoomId: room.liveActivityRoomId,
      currentOperatorType: 1,
    }).then((res): LiveRoomLinkInfo => {
      const data = (res.data || {}) as SaleActivityExpertLiveRoomInfo;
      const teacherLiveUrlInfo = data.teacherLiveUrlInfo;
      return {
        liveRoomName: data.liveRoomName || room.liveRoomName,
        liveUrl: teacherLiveUrlInfo?.liveUrl,
        password: teacherLiveUrlInfo?.password || getRoomPassword(room),
      };
    });

  const getUserRoomLinkInfo = (room: SaleActivityLiveRoomInfo) =>
    fetchSaleActivityUserEnterLiveRoomUrl({
      saleActivityId: room.saleActivityId || saleActivityId,
      saleActivityDetailId: room.saleActivityDetailId || saleActivityDetailId,
      liveActivityRoomId: room.liveActivityRoomId,
    }).then((res): LiveRoomLinkInfo => {
      return {
        liveRoomName: room.liveRoomName,
        liveUrl: res.data?.enterLiveRoomUrl,
        password: getRoomPassword(room),
      };
    });

  const openRoomLinkModal = (room: SaleActivityLiveRoomInfo) => {
    getExpertRoomLinkInfo(room).then((info) => {
      setLinkModalInfo(info);
      setLinkModalVisible(true);
    });
  };

  const jumpLiveRoom = (room: SaleActivityLiveRoomInfo) => {
    getUserRoomLinkInfo(room).then((info) => {
      if (!info.liveUrl) {
        message.warning("暂无可进入的直播间链接");
        return;
      }
      window.open(info.liveUrl, "_blank");
    });
  };

  const jumpCover = (room: SaleActivityLiveRoomInfo) => {
    if (hasUserRole) {
      jumpLiveRoom(room);
      return;
    }
    getExpertRoomLinkInfo(room).then((info) => {
      if (!info.liveUrl) {
        message.warning("暂无可进入的直播间链接");
        return;
      }
      window.open(info.liveUrl, "_blank");
    });
  };

  const copyLiveUrl = () => {
    if (!liveUrl) {
      message.warning("暂无可复制的直播链接");
      return;
    }
    navigator.clipboard
      ?.writeText(liveUrl)
      .then(() => message.success("链接已复制"))
      .catch(() => message.warning("复制失败，请手动复制链接"));
  };

  const openLiveUrl = () => {
    if (!liveUrl) {
      message.warning("暂无直播链接");
      return;
    }
    window.open(liveUrl, "_blank");
  };

  return (
    <div className="sale-activity-learn-page">
      <div className="sale-activity-learn-breadcrumb">
        <Breadcrumb separator=">">
          <Breadcrumb.Item>我的活动</Breadcrumb.Item>
          <Breadcrumb.Item>活动详情</Breadcrumb.Item>
          <Breadcrumb.Item>直播间列表</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <div className="sale-activity-live-room-page">
        {/* <Tabs activeKey={type} className="sale-activity-live-room-tabs">
          <Tabs.TabPane key="expert" tab="专家活动" />
          <Tabs.TabPane key="user" tab="学员活动" />
        </Tabs> */}

        <div className="sale-activity-live-room-head">
          <div>
            <h2>{liveActivityName}</h2>
            <p>直播间列表 · {roomCountText}</p>
          </div>
        </div>

        {roomList.length > 0 ? (
          <div className="sale-activity-live-room-grid">
            {roomList.map((room) => (
              <div
                className="sale-activity-live-room-card"
                key={room.liveActivityRoomId}
              >
                <button
                  type="button"
                  className="sale-activity-live-room-card__cover"
                >
                  <img
                    src={
                      room.liveImage ||
                      room.backgroundPc ||
                      cover ||
                      projectDefault
                    }
                    alt={room.liveRoomName || "直播间"}
                  />
                  {/* <span>{hasUserRole ? "进入直播间" : "直播信息"}</span> */}
                </button>
                <h3>{room.liveRoomName || "直播间名称"}</h3>
                <p>
                  直播间ID：
                  {textOrDash(room.ccRoomId || room.liveActivityRoomId)}
                </p>
                <p>开始时间：{textOrDash(room.liveBeginTime)}</p>
                <div className="sale-activity-live-room-card__actions">
                  {hasExpertRole && (
                    <button
                      type="button"
                      className="sale-activity-live-room-card__action"
                      onClick={() => openRoomLinkModal(room)}
                    >
                      获取专家链接
                    </button>
                  )}
                  {hasUserRole && (
                    <button
                      type="button"
                      className="sale-activity-live-room-card__action"
                      onClick={() => jumpLiveRoom(room)}
                    >
                      {hasExpertRole ? "学员进入直播" : "进入直播"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty description={loading ? "直播间加载中" : "暂无直播间"} />
        )}
      </div>

      <Modal
        title={linkModalInfo?.liveRoomName || "直播间信息"}
        visible={linkModalVisible}
        onCancel={() => setLinkModalVisible(false)}
        width={620}
        footer={[
          <Button
            key="open"
            type="primary"
            icon={<LinkOutlined />}
            disabled={!liveUrl}
            onClick={openLiveUrl}
          >
            打开链接
          </Button>,
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            disabled={!liveUrl}
            onClick={copyLiveUrl}
          >
            复制链接
          </Button>,
          <Button key="cancel" onClick={() => setLinkModalVisible(false)}>
            关闭
          </Button>,
        ]}
      >
        <div className="sale-activity-live-modal">
          <div className="sale-activity-live-modal__row">
            <label>直播链接：</label>
            {liveUrl ? (
              <a href={liveUrl} target="_blank" rel="noreferrer">
                {liveUrl}
              </a>
            ) : (
              <span>暂无直播链接</span>
            )}
          </div>
          <div className="sale-activity-live-modal__row">
            <label>直播密码：</label>
            <span>{linkModalInfo?.password || "--"}</span>
          </div>
        </div>
      </Modal>
    </div>
  );
}
